import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../services/axiosInstance';
import { API_ENDPOINTS } from '../config/api';
import { queryKeys } from '../config/queryClient';

const ph = API_ENDPOINTS.PREVENTIVE_HEALTH;

// ── Service calls ──────────────────────────────────────────────────────────────

const getUpcomingTasks = async (params?: any) => {
  const res = await axiosInstance.get(ph.TASKS_UPCOMING, { params });
  return res.data.data;
};

const getTodayTasks = async (params?: any) => {
  const res = await axiosInstance.get(ph.TASKS_TODAY, { params });
  return res.data.data;
};

const getBatchSchedule = async (batchId: number, params?: any) => {
  const res = await axiosInstance.get(ph.BATCH_SCHEDULE(batchId), { params });
  return res.data.data;
};

const getBatchUpcoming = async (batchId: number, params?: any) => {
  const res = await axiosInstance.get(ph.BATCH_UPCOMING(batchId), { params });
  return res.data.data;
};

const getProgrammes = async () => {
  const res = await axiosInstance.get(ph.PROGRAMMES);
  return res.data.data;
};

const getProgramme = async (id: number) => {
  const res = await axiosInstance.get(ph.PROGRAMME(id));
  return res.data.data;
};

const getAlerts = async (params?: any) => {
  const res = await axiosInstance.get(ph.ALERTS, { params });
  return res.data.data;
};

const getWithdrawalStatus = async (params?: any) => {
  const res = await axiosInstance.get(ph.REPORT_WITHDRAWAL, { params });
  return res.data.data;
};

const getCompliance = async (params?: any) => {
  const res = await axiosInstance.get(ph.REPORT_COMPLIANCE, { params });
  return res.data.data;
};

const getTreatmentHistory = async (params?: any) => {
  const res = await axiosInstance.get(ph.REPORT_TREATMENTS, { params });
  return res.data.data;
};

const getProducts = async (params?: any) => {
  const res = await axiosInstance.get(ph.PRODUCTS, { params });
  return res.data.data;
};

// ── Hooks ──────────────────────────────────────────────────────────────────────

export const useUpcomingTasks = (params?: any) =>
  useQuery({
    queryKey: queryKeys.ph.upcomingTasks(params),
    queryFn: () => getUpcomingTasks(params),
    enabled: params !== undefined,
  });

export const useTodayTasks = (params?: any) =>
  useQuery({
    queryKey: queryKeys.ph.todayTasks(params),
    queryFn: () => getTodayTasks(params),
  });

export const useBatchSchedule = (batchId: number | undefined, params?: any) =>
  useQuery({
    queryKey: queryKeys.ph.batchSchedule(batchId ?? 0, params),
    queryFn: () => getBatchSchedule(batchId ?? 0, params),
    enabled: !!batchId,
  });

export const useBatchUpcoming = (batchId: number | undefined, params?: any) =>
  useQuery({
    queryKey: queryKeys.ph.batchUpcoming(batchId ?? 0, params),
    queryFn: () => getBatchUpcoming(batchId ?? 0, params),
    enabled: !!batchId,
  });

export const usePreventiveProgrammes = () =>
  useQuery({
    queryKey: queryKeys.ph.programmes,
    queryFn: getProgrammes,
  });

export const usePreventiveProgramme = (id: number | undefined) =>
  useQuery({
    queryKey: queryKeys.ph.programme(id ?? 0),
    queryFn: () => getProgramme(id ?? 0),
    enabled: !!id,
  });

export const usePHAlerts = (params?: any) =>
  useQuery({
    queryKey: queryKeys.ph.alerts(params),
    queryFn: () => getAlerts(params),
  });

export const useWithdrawalStatus = (params?: any) =>
  useQuery({
    queryKey: queryKeys.ph.reportWithdrawal(params),
    queryFn: () => getWithdrawalStatus(params),
    enabled: params?.batch_id !== undefined,
  });

export const useCompliance = (params?: any) =>
  useQuery({
    queryKey: queryKeys.ph.reportCompliance(params),
    queryFn: () => getCompliance(params),
  });

export const useTreatmentHistory = (params?: any) =>
  useQuery({
    queryKey: queryKeys.ph.reportTreatments(params),
    queryFn: () => getTreatmentHistory(params),
  });

export const usePHProducts = (params?: any) =>
  useQuery({
    queryKey: queryKeys.ph.products(params),
    queryFn: () => getProducts(params),
  });

// ── Mutations ──────────────────────────────────────────────────────────────────

export const useAcknowledgeAlert = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (alertId: number) =>
      axiosInstance.post(ph.ALERT_ACKNOWLEDGE(alertId)).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ph', 'alerts'] }),
  });
};

export const useResolveAlert = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) =>
      axiosInstance.post(ph.ALERT_RESOLVE(id), { note }).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ph', 'alerts'] }),
  });
};

export const useAssignTask = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ eventId, userId }: { eventId: number; userId: number | null }) =>
      axiosInstance.patch(ph.EVENT_ASSIGN(eventId), { user_id: userId }).then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ph', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['ph', 'schedule'] });
    },
  });
};

export const useStartEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (eventId: number) =>
      axiosInstance.post(ph.EVENT_START(eventId)).then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ph', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['ph', 'schedule'] });
    },
  });
};

export const useSkipEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) =>
      axiosInstance.post(ph.EVENT_SKIP(id), { reason }).then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ph', 'tasks'] });
      qc.invalidateQueries({ queryKey: ['ph', 'schedule'] });
    },
  });
};

export const useUpdateProgramme = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      axiosInstance.patch(ph.PROGRAMME(id), data).then((r) => r.data.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ph', 'programmes'] });
      qc.invalidateQueries({ queryKey: ['ph', 'programme'] });
    },
  });
};

export const useStoreProgramme = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) => axiosInstance.post(ph.PROGRAMMES, data).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ph', 'programmes'] }),
  });
};

export const useUpdateProgrammeEvent = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      axiosInstance.patch(ph.PROGRAMME_EVENT(id), data).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ph', 'programme'] }),
  });
};

export const useStoreProgrammeEvent = () =>
  useMutation({
    mutationFn: ({ programmeId, data }: { programmeId: number; data: any }) =>
      axiosInstance.post(ph.PROGRAMME_EVENTS(programmeId), data).then((r) => r.data.data),
  });

export const useRegenerateProgramme = () =>
  useMutation({
    mutationFn: (id: number) => axiosInstance.post(ph.PROGRAMME_REGENERATE(id)).then((r) => r.data),
  });

export const useRegenerateBatch = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchId: number) =>
      axiosInstance.post(ph.BATCH_REGENERATE(batchId)).then((r) => r.data),
    onSuccess: (_data, batchId) => {
      qc.invalidateQueries({ queryKey: ['ph', 'schedule', batchId] });
      qc.invalidateQueries({ queryKey: ['ph', 'upcoming', batchId] });
    },
  });
};

export const useAssignBatchProgramme = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ batchId, programmeId }: { batchId: number; programmeId: number | null }) =>
      axiosInstance
        .patch(ph.BATCH_ASSIGN_PROGRAMME(batchId), { programme_id: programmeId })
        .then((r) => r.data.data),
    onSuccess: (_data, { batchId }) => {
      qc.invalidateQueries({ queryKey: ['bird-batches', batchId] });
      qc.invalidateQueries({ queryKey: ['ph', 'schedule', batchId] });
      qc.invalidateQueries({ queryKey: ['ph', 'upcoming', batchId] });
    },
  });
};

export const useAlertThresholds = () =>
  useQuery({
    queryKey: ['ph', 'alert-thresholds'],
    queryFn: () => axiosInstance.get(ph.ALERT_THRESHOLDS).then((r) => r.data.data),
  });

export const useStoreAlertThreshold = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: any) =>
      axiosInstance.post(ph.ALERT_THRESHOLDS, data).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ph', 'alert-thresholds'] }),
  });
};

export const useUpdateAlertThreshold = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      axiosInstance.patch(ph.ALERT_THRESHOLD(id), data).then((r) => r.data.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ph', 'alert-thresholds'] }),
  });
};

export const useDeleteAlertThreshold = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => axiosInstance.delete(ph.ALERT_THRESHOLD(id)).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ph', 'alert-thresholds'] }),
  });
};
